import { Puzzle, ReplayEvent } from '../shared/types';
import { CellState, cloneBoard, queenAttacks } from '../shared/board';

// The board primitives live in src/shared/board.ts so the server and the offline analyzer can
// use them too; re-exported here because the client has always imported them from './game'.
export { cloneBoard, computeAutoXs, queenAttacks } from '../shared/board';
export type { CellState } from '../shared/board';

export interface Conflict {
  row: number;
  col: number;
  reason: 'row' | 'col' | 'color' | 'diagonal';
}

export interface ValidationResult {
  // Set of "row,col" strings for all cells that are part of a conflict
  invalidCells: Set<string>;
  // Set of "row,col" strings for cells to show crosshatch (the conflicting area)
  crosshatchCells: Set<string>;
  solved: boolean;
}

export interface GameState {
  board: CellState[][];
  undoStack: CellState[][][];
  events: ReplayEvent[];
  startTime: number;
  elapsedAtComplete: number | null;
}

export function createGameState(puzzle: Puzzle): GameState {
  return {
    board: Array.from({ length: puzzle.size }, () => Array(puzzle.size).fill(null)),
    undoStack: [],
    events: [],
    startTime: Date.now(),
    elapsedAtComplete: null,
  };
}

function elapsed(state: GameState): number {
  return Date.now() - state.startTime;
}

export function applyClick(state: GameState, row: number, col: number): GameState {
  const board = cloneBoard(state.board);
  const prev = board[row][col];
  board[row][col] = prev === null ? 'X' : prev === 'X' ? 'queen' : null;

  return {
    ...state,
    board,
    undoStack: [...state.undoStack, state.board],
    events: [...state.events, { type: 'click', row, col, elapsedMs: elapsed(state) }],
  };
}

export function applyDrag(state: GameState, cells: Array<{ row: number; col: number }>): GameState {
  if (cells.length === 0) return state;

  const board = cloneBoard(state.board);
  const affected: Array<{ row: number; col: number }> = [];

  for (const { row, col } of cells) {
    if (board[row][col] === null) {
      board[row][col] = 'X';
      affected.push({ row, col });
    }
  }

  if (affected.length === 0) return state;

  return {
    ...state,
    board,
    undoStack: [...state.undoStack, state.board],
    events: [...state.events, { type: 'drag', cells: affected, elapsedMs: elapsed(state) }],
  };
}

// Apply a hint's suggested marks. 'eliminate' hints X out every target cell that's still
// empty; 'place' hints drop a queen on the single forced cell and X out the squares it attacks.
// The change is recorded as ordinary replay events (a drag of Xs, or the clicks that cycle a cell
// up to a queen) and pushed as one undo step, so it replays and undoes exactly like manual play.
export function applyHint(state: GameState, kind: 'eliminate' | 'place', cells: string[], colorRegions: string[][], rule?: string): GameState {
  if (cells.length === 0) return state;
  const board = cloneBoard(state.board);
  const elapsedMs = elapsed(state);
  // Stamp the provenance so replay analysis can tell a deduction the player made from one the
  // panel handed them — otherwise hint use inflates every technique the panel happens to show.
  const src = { source: 'hint', rule } as const;
  const parse = (k: string): [number, number] => {
    const [r, c] = k.split(',');
    return [Number(r), Number(c)];
  };

  if (kind === 'place') {
    const [r, c] = parse(cells[0]);
    if (board[r][c] === 'queen') return state;
    const events: ReplayEvent[] = [];
    while (board[r][c] !== 'queen') {
      board[r][c] = board[r][c] === null ? 'X' : 'queen';
      events.push({ type: 'click', row: r, col: c, elapsedMs, ...src });
    }
    // Mark what the new queen rules out. Without this the hint engine — which reasons only from
    // visible marks — sees the attacked squares as still available, so the board can stall with
    // no further hints (and the player is left doing the bookkeeping by hand).
    const attacked: Array<{ row: number; col: number }> = [];
    for (const k of queenAttacks(r, c, colorRegions)) {
      const [ar, ac] = parse(k);
      if (board[ar][ac] === null) {
        board[ar][ac] = 'X';
        attacked.push({ row: ar, col: ac });
      }
    }
    if (attacked.length) events.push({ type: 'drag', cells: attacked, elapsedMs, ...src });
    return {
      ...state,
      board,
      undoStack: [...state.undoStack, state.board],
      events: [...state.events, ...events],
    };
  }

  const affected: Array<{ row: number; col: number }> = [];
  for (const k of cells) {
    const [r, c] = parse(k);
    if (board[r][c] === null) {
      board[r][c] = 'X';
      affected.push({ row: r, col: c });
    }
  }
  if (affected.length === 0) return state;
  return {
    ...state,
    board,
    undoStack: [...state.undoStack, state.board],
    events: [...state.events, { type: 'drag', cells: affected, elapsedMs, ...src }],
  };
}

/**
 * Put a new player's revealed queens on the board (see src/client/reveal.ts).
 *
 * Recorded as ordinary hint-sourced clicks at t=0 rather than as an event type of its own: the
 * replay player and the analyzer both reconstruct the board by replaying events from empty, so a
 * type either of them skipped would leave those queens missing from every replay of the run. The
 * `source: 'hint'` stamp is what keeps attribution from crediting the player with queens they
 * were handed. Unlike a hint, this marks nothing the queens attack — the reveal gives away the
 * placements only, and the Auto Xs setting already covers the bookkeeping for whoever wants it.
 *
 * No undo entry: this *is* the starting position, so there is nothing behind it to undo to.
 */
export function applyReveal(state: GameState, cells: string[]): GameState {
  if (cells.length === 0) return state;
  const board = cloneBoard(state.board);
  const events: ReplayEvent[] = [];
  for (const k of cells) {
    const [r, c] = k.split(',').map(Number);
    if (board[r][c] === 'queen') continue;
    while (board[r][c] !== 'queen') {
      board[r][c] = board[r][c] === null ? 'X' : 'queen';
      events.push({ type: 'click', row: r, col: c, elapsedMs: 0, source: 'hint', rule: 'reveal' });
    }
  }
  return { ...state, board, events: [...state.events, ...events] };
}

/**
 * Record an assist setting's value. Pushed at its initial value and on every toggle, so replay
 * analysis can see which mode the player was actually in at each moment — the single
 * `used_auto_xs` column on the completion cannot express a mid-game change. Not a board change,
 * so it takes no undo slot and consumers that do not know the type ignore it.
 */
export function applySetting(state: GameState, setting: 'autoXs' | 'hints', on: boolean): GameState {
  return {
    ...state,
    events: [...state.events, { type: 'setting', setting, on, elapsedMs: elapsed(state) }],
  };
}

export function applyClear(state: GameState): GameState {
  const size = state.board.length;
  return {
    ...state,
    board: Array.from({ length: size }, () => Array(size).fill(null)),
    undoStack: [...state.undoStack, state.board],
    elapsedAtComplete: null,
    events: [...state.events, { type: 'clear', elapsedMs: elapsed(state) }],
  };
}

export function applyUndo(state: GameState): GameState {
  if (state.undoStack.length === 0) return state;

  const undoStack = [...state.undoStack];
  const board = undoStack.pop()!;

  return {
    ...state,
    board,
    undoStack,
    elapsedAtComplete: null,
    events: [...state.events, { type: 'undo', elapsedMs: elapsed(state) }],
  };
}

export function validate(board: CellState[][], colorRegions: string[][]): ValidationResult {
  const size = board.length;
  const queens: Array<{ row: number; col: number }> = [];

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (board[r][c] === 'queen') queens.push({ row: r, col: c });
    }
  }

  const invalid = new Set<string>();
  const crosshatch = new Set<string>();

  const key = (r: number, c: number) => `${r},${c}`;

  for (let i = 0; i < queens.length; i++) {
    for (let j = i + 1; j < queens.length; j++) {
      const a = queens[i];
      const b = queens[j];

      if (a.row === b.row) {
        invalid.add(key(a.row, a.col));
        invalid.add(key(b.row, b.col));
        for (let c = 0; c < size; c++) crosshatch.add(key(a.row, c));
      }
      if (a.col === b.col) {
        invalid.add(key(a.row, a.col));
        invalid.add(key(b.row, b.col));
        for (let r = 0; r < size; r++) crosshatch.add(key(r, a.col));
      }
      if (colorRegions[a.row][a.col] === colorRegions[b.row][b.col]) {
        invalid.add(key(a.row, a.col));
        invalid.add(key(b.row, b.col));
        const color = colorRegions[a.row][a.col];
        for (let r = 0; r < size; r++)
          for (let c = 0; c < size; c++)
            if (colorRegions[r][c] === color) crosshatch.add(key(r, c));
      }
      // diagonal adjacency (only immediate diagonals)
      if (Math.abs(a.row - b.row) === 1 && Math.abs(a.col - b.col) === 1) {
        invalid.add(key(a.row, a.col));
        invalid.add(key(b.row, b.col));
        crosshatch.add(key(a.row, a.col));
        crosshatch.add(key(b.row, b.col));
      }
    }
  }

  const solved = queens.length === size && invalid.size === 0;

  return { invalidCells: invalid, crosshatchCells: crosshatch, solved };
}
